import Model from 'src/app/common/model';
import DeviceModel from './device.model';
import SaleTypeModel from './saleType.model';
import CustomerPondModel from './customerPond.model';
import FarmSiteModel from './farmSite.model';
import BusinessEntity from './businessEntity.model';
import Device from './device.model';

/**
 * @model CustomerDeviceModel
 * @description
 * Represents the specific instance of a device assigned to a customer (Business Entity) within the AquaCloud application.
 * Unlike `DeviceModel` which defines the hardware specifications (Model X, Version Y), `CustomerDeviceModel`
 * represents the "deployed" state of that hardware.
 *
 * **Domain Context:**
 * In aquaculture, devices (sensors, feeders, controllers) are assets that move through a lifecycle:
 * Inventory -> Sold/Rented -> Assigned to Farm -> Deployed in Pond -> Returned/Maintenance.
 * This model captures the state of the device *after* it leaves the warehouse and is associated with a user.
 *
 * **Purpose:**
 * To manage the relationship between a physical device and its operational environment.
 * It answers: "Who owns this device?", "Where is it located?", "Is it currently monitoring?", "Is it a trial unit?".
 *
 * **Key Responsibilities:**
 * 1. **Ownership & Location**: Links `device_id` to `business_id`, `farm_site_id`, and `pond_id`.
 * 2. **Operational State**: Tracks if the device is in "Continuous Monitoring" (CM) mode or "Spot Testing" mode via `isContinuousMonitoring` and `cm_running`.
 * 3. **Commercial Terms**: Defines if the device is sold, rented, or on trial via `saleType` and `isTrail`.
 * 4. **Grouping**: Can aggregate lists of specific device types (e.g., `aquDevices`, `tintoDevices`) for UI display.
 *
 * **Data Flow:**
 * - **Assignment**: Created/Updated when an Admin assigns a device to a Business, or a Farmer assigns it to a Pond.
 * - **Configuration**: Updated when users change settings like `test_interval`.
 * - **Monitoring**: Used by the backend to validate if incoming telemetry data matches an active subscription/assignment.
 *
 * **User Roles & Permissions:**
 * - **System Admin**: Can map devices to businesses (`isAssigned`).
 * - **Farm Manager**: Can map devices to specific ponds and toggle CM mode.
 *
 * **Features:**
 * - Supports Master/Slave device configurations (`isMaster`, `paired_device_id`).
 * - Handles different device families (Aqua, Tinto, Calorimeter).
 * - Tracks barcode sequences for inventory reconciliation.
 *
 * @extends Model
 * @author AquaCloud Development Team
 * @version 1.0.0
 */
export default class CustomerDeviceModel extends Model {
  /**
   * @type {DeviceModel}
   * @description The hardware definition/specification of the device.
   */
  device: DeviceModel;

  /**
   * @type {Array<CustomerDeviceModel>}
   * @description Collection of detailed device records, often used when this model acts as a wrapper or response container.
   */
  DeviceDetails: Array<CustomerDeviceModel>;

  /**
   * @type {Array<FarmSiteModel>}
   * @description List of farm sites associated with this device or available for assignment.
   */
  FarmSiteDetails: Array<FarmSiteModel>;

  /**
   * @type {Array<BusinessEntity>}
   * @description List of business entities associated with this device.
   */
  businessIds: Array<BusinessEntity>;

  /**
   * @type {SaleTypeModel}
   * @description The commercial terms under which the device is held (e.g., Purchase, Rent, Lease).
   */
  saleType: SaleTypeModel;

  /**
   * @type {Array<CustomerDeviceModel>}
   * @description Specific collection of 'Aqua' family devices.
   */
  aquDevices: Array<CustomerDeviceModel>;

  /**
   * @type {Array<CustomerDeviceModel>}
   * @description Specific collection of 'Tinto' family devices.
   */
  tintoDevices: Array<CustomerDeviceModel>;

  /**
   * @type {CustomerPondModel}
   * @description The specific pond where this device is currently deployed.
   */
  customerPond: CustomerPondModel;

  /**
   * @type {Array<any>}
   * @description Array of device identifiers. Type is `any` to handle legacy string/number formats.
   */
  device_id: Array<any>;

  /**
   * @type {string}
   * @description Human-readable name given to the device (e.g., "Pond 1 Sensor").
   * @optional
   */
  device_name?: string;

  /**
   * @type {number}
   * @description Foreign key for the Sale Type.
   */
  sale_type_id: number;

  /**
   * @type {string}
   * @description The unique identifier of the business entity that owns/leases the device.
   * @optional
   */
  business_id?: string;

  /**
   * @type {boolean}
   * @description Flag indicating if this device acts as a Master node in a mesh network.
   */
  isMaster: boolean;

  /**
   * @type {boolean}
   * @description Flag indicating if this device is currently on a trial period.
   */
  isTrail: boolean;

  /**
   * @type {boolean}
   * @description Flag indicating if the device has been successfully assigned to a customer.
   */
  isAssigned: boolean;

  /**
   * @type {string}
   * @description The unique identifier of the pond where the device is located.
   */
  pond_id: string;

  /**
   * @type {CustomerPondModel}
   * @description Reference to the full pond object.
   */
  pond: CustomerPondModel;

  /**
   * @type {number}
   * @description The frequency (in minutes) at which the device takes readings in CM mode.
   * @optional
   */
  test_interval?: number;

  /**
   * @type {boolean}
   * @description Flag indicating if the Continuous Monitoring process is currently active/running.
   */
  cm_running: boolean;

  /**
   * @type {boolean}
   * @description Configuration flag to enable/disable Continuous Monitoring capabilities for this device.
   */
  isContinuousMonitoring: boolean;

  /**
   * @type {string}
   * @description Display name of the business entity.
   */
  businessName: string;

  /**
   * @type {any}
   * @description Type classification of the business (e.g., Farm, Hatchery).
   */
  businessType: any;

  /**
   * @type {number}
   * @description Numeric flag or status code related to Continuous Monitoring (CM).
   */
  isCM: number;

  /**
   * @type {string}
   * @description ID of the service provider associated with this device.
   */
  providerId: string;

  /**
   * @type {string}
   * @description The barcode sequence associated with the device for inventory tracking.
   */
  barcode_sequence: string;

  /**
   * @type {string}
   * @description The device serial number or hardware identifier.
   */
  number: string;

  /**
   * @type {Device}
   * @description Reference to a calorimeter device if this record represents one.
   */
  calorimeterDevice: Device;

  /**
   * @type {string}
   * @description ID of another device paired with this one (e.g., a probe paired with a transmitter).
   */
  paired_device_id: string;

  /**
   * @type {string}
   * @description Selection flag for UI logic, likely distinguishing between 'CM' (Continuous Monitoring) or 'FP' (Farm Pond/Profile).
   */
  selectedCMorFP: string;

  /**
   * @type {string}
   * @description The ID of the business type.
   */
  businessTypeID: string;

  /**
   * @constructor
   * @description
   * Initializes a new instance of the CustomerDeviceModel.
   * Calls the parent `Model` constructor.
   */
  constructor() {
    super();
  }
}
